import React, { useState, useEffect } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { inspectionWizardFields } from "./inspectionWizardConfig";
import useInspectionWizard from "./useInspectionWizard";
import { Dialog } from "@mui/material";
import { selectNavHeight } from "../../../../../../redux/navbar/navHeightSlice";
import { useSelector } from "react-redux";
import RenderFields from "../../../../../../components/Modals/ModalFields/RenderFields";
import API_BASE_URL from "../../../../../../Constants";

// Define fields for building details to be used with RenderFields
const getBuildingDetailFields = (building, agency) => {
  return [
    {
      key: "buildingReference",
      label: "Building Site",
      type: "text",
      forceReadOnly: true,
    },
    {
      key: "agencyName",
      label: "Agency Name",
      type: "text",
      forceReadOnly: true,
    },
    {
      key: "managementCompanyName",
      label: "Management",
      type: "text",
      forceReadOnly: true,
    },
  ];
};

const WizardHeader = ({ label, onClose }) => {
  return (
    <div className="flex items-center p-4 border-b relative">
      <button
        onClick={onClose}
        className="p-2 bg-gray-300 rounded-full hover:bg-cyan-100"
      >
        <ArrowBackIcon className="text-gray-700" />
      </button>
      <h2 className="text-xl absolute left-1/2 transform -translate-x-1/2">
        {label}
      </h2>
    </div>
  );
};

const BuildingDetails = ({ building, agency }) => {
  // Create data object to use with RenderFields
  const buildingData = {
    buildingReference: building?.buildingReference || "(loading)",
    agencyName: agency?.agencyName || "(none)",
    managementCompanyName: building?.managementCompany?.companyName || "(none)",
  };

  const buildingDetailFields = getBuildingDetailFields(building, agency);

  return (
    <div className="border rounded p-4 bg-white">
      <h3 className="mb-2 text-lg">Building Information</h3>
      <RenderFields
        fields={buildingDetailFields}
        formData={buildingData}
        handleChange={() => {}} // No-op function since fields are read-only
        readOnly={true}
      />
    </div>
  );
};

const DevicesList = ({ devices }) => {
  return (
    <div className="border rounded p-4 bg-white">
      <h3 className="mb-2 text-lg">Devices ({devices.length})</h3>
      <div className="max-h-[150px] overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 py-1">
          {devices.map((dev) => (
            <div
              key={dev.deviceId}
              className="flex justify-center items-center"
            >
              <span className="w-full text-sm border border-gray-300 p-1 shadow-md text-center">
                {`ID: ${dev.deviceId} - ${dev?.deviceName}`}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const WizardUserInput = ({ formData, setFormData, agencyTests }) => {
  // Create a copy of the fields to modify dynamically based on agency tests
  const [fields, setFields] = useState([...inspectionWizardFields]);

  // Update the inspection type options when agency tests change
  useEffect(() => {
    if (agencyTests && agencyTests.length > 0) {
      const updatedFields = [...inspectionWizardFields];
      // Find the inspectionType field
      const inspectionTypeField = updatedFields.find(
        (field) => field.key === "inspectionType"
      );
      if (inspectionTypeField) {
        // Start with the "All Available Inspections" option
        inspectionTypeField.options = [
          { value: "all", label: "All Available Inspections" },
        ];
        // Add agency tests as options
        agencyTests.forEach((test) => {
          inspectionTypeField.options.push({
            value: test.testId,
            label: test?.label || "Unknown Inspection",
          });
        });
      }
      setFields(updatedFields);
    }
  }, [agencyTests]);

  // Handle form changes - updated to handle event object from RenderFields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="border rounded p-4 bg-white">
      <h3 className="mb-2 text-lg">Inspection Details</h3>
      <RenderFields
        fields={fields}
        formData={formData}
        handleChange={handleChange}
      />
    </div>
  );
};

const ScheduledInspectionsSection = ({
  scheduledInspections,
  devicesWithoutInspection,
  isLoading,
  formData,
  agencyTests,
}) => {
  // Format date for readable display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Get frequency text based on agencyTests
  const getFrequencyText = (testId) => {
    if (!testId || !agencyTests) return "Unknown";
    const test = agencyTests.find((test) => test.testId === Number(testId));
    return test?.frequency || "Unknown";
  };

  // Determine if inspection creation is needed
  const needsInspectionCreation = devicesWithoutInspection.length > 0;

  return (
    <div className="border rounded p-4 bg-white">
      <h3 className="mb-2 text-lg">
        Scheduled Inspections for {formData.inspYear}
      </h3>

      {isLoading ? (
        <div className="flex justify-center p-4">
          <p>Loading inspections...</p>
        </div>
      ) : (
        <>
          {/* Display already scheduled inspections */}
          {scheduledInspections.length > 0 ? (
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Existing Inspections</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {scheduledInspections.map((inspection) => (
                  <div
                    key={inspection.inspectionId}
                    className="border p-3 rounded-lg bg-blue-50"
                  >
                    <div className="font-medium mb-1">
                      {inspection.testName || "Inspection"}
                      <span className="ml-2 text-sm bg-blue-200 text-blue-800 px-2 py-0.5 rounded">
                        {inspection.status || "Planned"}
                      </span>
                    </div>
                    <div className="text-sm mb-1">
                      <span className="font-semibold">Schedule Date:</span>{" "}
                      {formatDate(inspection.scheduledDate)}
                    </div>
                    <div className="text-sm mb-1">
                      <span className="font-semibold">Frequency:</span>{" "}
                      {getFrequencyText(inspection.testId)}
                    </div>
                    <div className="text-sm mt-2">
                      <span className="font-semibold">Devices:</span>{" "}
                      {inspection.devices?.length || 0} devices covered
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="italic text-gray-500 mb-4">
              No inspections scheduled for {formData.inspYear} yet.
            </p>
          )}

          {/* Display devices without inspection */}
          <div className="mt-4">
            <h4 className="font-semibold mb-2">Devices Requiring Inspection</h4>
            {needsInspectionCreation ? (
              <div>
                <div className="bg-yellow-50 border border-yellow-300 p-3 rounded mb-3">
                  <p className="font-semibold text-yellow-800">
                    {devicesWithoutInspection.length} device(s) require
                    inspection scheduling
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    When you submit this form, inspections will be created for
                    these devices according to the inspection type and task type
                    you've selected.
                  </p>
                </div>

                <div className="max-h-[150px] overflow-y-auto border rounded p-2 bg-gray-50">
                  {devicesWithoutInspection.map((device) => (
                    <div
                      key={device.deviceId}
                      className="text-sm py-1 border-b last:border-0"
                    >
                      ID: {device.deviceId} -{" "}
                      {device.deviceName ||
                        device.deviceReference ||
                        "Unnamed device"}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="italic text-gray-500">
                All devices have inspections scheduled for {formData.inspYear}.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const InspectionWizard = ({ isOpen, building, onClose, refreshList }) => {
  // import the custom hook to manage the inspection wizard
  const {
    agency,
    agencyTests,
    devices,
    formData,
    setFormData,
    scheduledInspections,
    devicesWithoutInspection,
    isLoading,
  } = useInspectionWizard(building);

  const navHeight = useSelector(selectNavHeight);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Handle submission to create inspections
  const handleCreateInspections = async () => {
    if (devicesWithoutInspection.length === 0) {
      setError("No devices require new inspections");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      // Parse numeric values to ensure correct types
      const payload = {
        buildingId: Number(building.buildingID),
        agencyId: Number(building.agencyID),
        managementCompanyId: building.managementCompany?.managementId || null, // Add management company ID
        deviceIds: devicesWithoutInspection.map((device) =>
          Number(device.deviceId)
        ),
        inspectionType: formData.inspectionType,
        taskType: formData.taskType,
        year: Number(formData.inspYear),
        approvalDate: formData.approvalDate || null,
      };

      console.log("Sending payload:", JSON.stringify(payload));

      const response = await fetch(
        `${API_BASE_URL}/api/inspections/createInspections`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          credentials: "include", // Include cookies for session-based auth
        }
      );

      if (!response.ok) {
        // Try to parse error details from response
        let errorText = "Failed to create inspections";
        try {
          const errorData = await response.json();
          errorText = errorData.message || errorData.details || errorText;
        } catch (e) {
          // If JSON parsing fails, use status text
          errorText = `Failed to create inspections: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorText);
      }

      // Successfully created inspections
      setSuccess(true);

      // Refresh the list after a short delay
      setTimeout(() => {
        if (refreshList) refreshList();
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Error creating inspections:", error);
      setError(error.message || "Failed to create inspections");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            position: "absolute",
            top: navHeight,
            left: "50%",
            transform: "translateX(-50%)",
            margin: 4,
            width: "1000px", // Use the default non-expanded size
            height: `calc(100vh - ${navHeight + 8}px)`,
            overflow: "hidden",
          },
        }}
        style={{ zIndex: 1600 }}
      >
        <WizardHeader label="Inspection Wizard" onClose={onClose} />
        <div
          className="p-4 space-y-4 overflow-y-auto bg-gray-50"
          style={{ maxHeight: `calc(100vh - ${navHeight + 120}px)` }}
        >
          <BuildingDetails building={building} agency={agency} />
          <DevicesList devices={devices} />
          <WizardUserInput
            formData={formData}
            setFormData={setFormData}
            agencyTests={agencyTests}
          />
          <ScheduledInspectionsSection
            scheduledInspections={scheduledInspections}
            devicesWithoutInspection={devicesWithoutInspection}
            isLoading={isLoading}
            formData={formData}
            agencyTests={agencyTests}
          />

          {/* Error and success messages */}
          {error && (
            <div className="bg-red-50 border border-red-300 text-red-800 p-3 rounded">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-300 text-green-800 p-3 rounded">
              Inspections successfully created! Closing wizard...
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateInspections}
              className={`px-4 py-2 bg-blue-600 text-white rounded ${
                isSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-blue-700"
              }`}
              disabled={isSubmitting || devicesWithoutInspection.length === 0}
            >
              {isSubmitting ? "Creating..." : "Create Inspections"}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default InspectionWizard;
